/**
 * Left-nav preferences — pure helpers (no supabase imports; unit-tested in
 * navPrefsPure.test.ts). Consumed by userEntityPreferencesService and the
 * sidebar/thread-header UI.
 */

export type NavigableEntityType = "space" | "topic" | "thread";
export type NavPriority = "none" | "low" | "medium" | "high" | "urgent";
export type SidebarThreadFilter = "all" | "favorites" | "flagged";

export interface NavPreference {
  entityType: NavigableEntityType;
  entityId: string;
  isFavorite: boolean;
  priority: NavPriority;
}

/** Severity order for the Flagged view (urgent first). */
export const PRIORITY_RANK: Record<NavPriority, number> = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1,
  none: 0,
};

export const PRIORITY_OPTIONS: Array<{ value: NavPriority; label: string }> = [
  { value: "none", label: "None" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

/**
 * Calm flag palette (amber/deep-orange family; never aggressive red, never
 * pure black/white, ≤85% saturation). Meaning is never color-alone — the UI
 * pairs these with text labels/tooltips.
 */
export const PRIORITY_COLORS: Record<Exclude<NavPriority, "none">, string> = {
  low: "hsl(45 70% 55%)",     // soft amber
  medium: "hsl(38 75% 50%)",  // amber
  high: "hsl(28 80% 50%)",    // deep orange
  urgent: "hsl(16 82% 47%)",  // burnt orange (not red)
};

/** Key for a prefs lookup map. */
export function prefKey(entityType: NavigableEntityType, entityId: string): string {
  return `${entityType}:${entityId}`;
}

/** Build a lookup map from a list of preference rows. */
export function buildPrefMap(prefs: NavPreference[]): Map<string, NavPreference> {
  const m = new Map<string, NavPreference>();
  for (const p of prefs) m.set(prefKey(p.entityType, p.entityId), p);
  return m;
}

export interface ThreadLike {
  id: string | number;
  updatedAt?: string | Date | null;
  createdAt?: string | Date | null;
}

function recency(t: ThreadLike): number {
  const raw = t.updatedAt ?? t.createdAt ?? "";
  if (raw instanceof Date) return raw.getTime();
  const v = Date.parse(String(raw));
  return Number.isNaN(v) ? 0 : v;
}

/**
 * Apply the sidebar filter to a thread list using the prefs map.
 * - all:       unchanged order (caller already sorts by recency)
 * - favorites: only starred threads, recency desc
 * - flagged:   only priority ≠ none, sorted Urgent→High→Medium→Low then recency
 * Never mutates its inputs.
 */
export function applySidebarFilter<T extends ThreadLike>(
  threads: T[],
  filter: SidebarThreadFilter,
  prefMap: Map<string, NavPreference>,
): T[] {
  if (filter === "all") return threads;
  const withPref = threads.map((t) => ({ t, p: prefMap.get(prefKey("thread", String(t.id))) }));
  if (filter === "favorites") {
    return withPref
      .filter(({ p }) => p?.isFavorite)
      .sort((a, b) => recency(b.t) - recency(a.t))
      .map(({ t }) => t);
  }
  // flagged
  return withPref
    .filter(({ p }) => p && p.priority !== "none")
    .sort((a, b) => {
      const d = PRIORITY_RANK[b.p!.priority] - PRIORITY_RANK[a.p!.priority];
      return d !== 0 ? d : recency(b.t) - recency(a.t);
    })
    .map(({ t }) => t);
}

/** Orphan guard: prefs whose target no longer appears in the visible list are ignored (never resurface targets). */
export function activeFilterCount(prefs: NavPreference[]): { favorites: number; flagged: number } {
  let favorites = 0;
  let flagged = 0;
  for (const p of prefs) {
    if (p.entityType !== "thread") continue;
    if (p.isFavorite) favorites++;
    if (p.priority !== "none") flagged++;
  }
  return { favorites, flagged };
}
